import Badge from '@mui/material/Badge';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Portal from '@mui/material/Portal';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { setMobileMenuState } from '../../../ducks/ui.duck';
import defaultSearchCountry from '../../../forms/HeroSearchForm/defaultSearchCountry';
import routeConfiguration from '../../../routeConfiguration';
import { petrol, yellow } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import useAppTheme from '../../../util/hooks/useAppTheme';
import { createResourceLocatorString, matchPathname } from '../../../util/routes';
import NoBookingsRegisterCTA from '../../CallToActions/EmptyStates/NoBookings/NoBookingsRegisterCTA';
import NoBookmarksRegisterCTA from '../../CallToActions/EmptyStates/NoBookmarks/NoBookmarksRegisterCTA';
import NoSpotsRegisterCTA from '../../CallToActions/EmptyStates/NoSpots/NoSpotsRegisterCTA';
import IconBookings from '../../Icons/IconBookings';
import IconFavorites from '../../Icons/IconFavorites';
import IconHome from '../../Icons/IconHome';
import IconMore from '../../Icons/IconMore';
import MobileMenu from '../MobileMenu/MobileMenu';
import SearchIconAction from './SearchIconAction';

/**
 * Styles for the Call-To-Action (CTA) boxes displayed in the drawers.
 * These styles center the content both vertically and horizontally,
 * set the background color, and ensure the content occupies the full viewport height.
 *
 * @type {Object}
 */
const boxStylesForCTAs = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  minHeight: '100vh',
  backgroundColor: '#fff',
  paddingBottom: 'var(--BottomBar_height)',
};

/**
 * Returns the style object for the icon text.
 *
 * @param {number} siteValue - The current active site value.
 * @param {number} activeValue - The value to compare with siteValue.
 * @param {boolean} [mobileMenuState=false] - The mobile menu state.
 * @returns {object} - The style object.
 */
const getIconTextStyle = (siteValue, activeValue, mobileMenuState = false) => {
  const isActive = siteValue === activeValue;
  const isMoreActive = activeValue === 4 && mobileMenuState;
  const color = isActive || isMoreActive ? yellow.main : petrol.alpha75;

  return {
    fontSize: '0.78rem',
    '@media (min-width:460px)': { fontSize: '1rem' },
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    color,
    transition: 'none',
  };
};

/**
 * Returns the icon color.
 *
 * @param {number} siteValue - The current active site value.
 * @param {number} activeValue - The value to compare with siteValue.
 * @param {boolean} [mobileMenuOpen=false] - The mobile menu open state.
 * @returns {string} - The icon color.
 */
const getIconColor = (siteValue, activeValue, mobileMenuState = false) => {
  const isActive = siteValue === activeValue;
  const isMoreActive = activeValue === 4 && mobileMenuState;
  return isActive || isMoreActive ? yellow.main : petrol.alpha75;
};
/**
 * Returns the bottom navigation style.
 *
 * @param {number} zIndexDrawer - The zIndex of the drawer from the theme.
 * @returns {object} - The style object.
 */
const getBottomNavigationStyle = (zIndexDrawer) => ({
  boxShadow: '0px 0 24px rgba(0,0,0,0.1)',
  zIndex: zIndexDrawer + 1,
  px: 0,
  transition: 'none',
  height: 'var(--BottomBar_height)',
});
const routeNames = ['LandingPage', 'BookmarkPage', 'SearchPage', 'InboxPage'];

/**
 * BottomNavigationBar is a navigation component that resides at the bottom of the screen.
 * It allows the user to navigate between different sections of the application and highlights the currently active section.
 * It uses the Material-UI BottomNavigation component for the implementation and custom icons for each section.
 *
 * It takes the following props:
 * - history: The history object from the react-router, used to navigate programmatically.
 * - intl: The intl object provided by react-intl, used for localization.
 *
 * The component maintains an internal state for the currently selected index in the navigation bar.
 * When a navigation item is clicked, it updates the internal state and navigates to the corresponding page using the history object.
 * The navigation items and their corresponding routes are defined in the `routeNames` array.
 * The corresponding route for a navigation item is determined using the `createResourceLocatorString` utility function, which generates a URL for a route.
 */
const BottomNavigationBarComponent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useAppTheme();

  const [openDrawer, setOpenDrawer] = useState(null); // 'becomeHost', 'bookmarks', 'bookings', or null

  const currentUserNotificationCount = useAppSelector(
    (state) => state.user.currentUserNotificationCount
  );
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { mobileMenuState, settingsMenuState } = useAppSelector(
    (state) => ({
      mobileMenuState: state.ui.mobileMenuState,
      settingsMenuState: state.ui.settingsMenuState,
    }),
    shallowEqual
  );
  const notificationCount =
    currentUserNotificationCount.provider + currentUserNotificationCount.customer;

  const currentRoute = matchPathname(location.pathname, routeConfiguration())[0].route.name;
  const [siteValue, setSiteValue] = useState(() => {
    const activeRoute = routeNames.indexOf(currentRoute);
    return activeRoute === -1 ? 4 : activeRoute;
  });

  /**
   * Handles the submission of search parameters by navigating to the SearchPage with the given parameters.
   *
   * @param {object} params - The search parameters.
   * @param {object} params.location - The location object containing address and bounds.
   * @param {object} params.dates - The dates object containing date range.
   * @param {object} params.state - Additional state parameters.
   */
  const handleSubmit = useCallback(
    ({ location, dates, state }) => {
      const searchParams = {
        dates: dates,
        address: location ? location.address : '',
        bounds: location ? location.bounds : '',
      };
      if (location || dates) {
        navigate(
          createResourceLocatorString(
            'SearchPage',
            routeConfiguration(),
            {},
            { ...searchParams, ...state },
            intl.locale
          ),
          { state }
        );
      }
    },
    [navigate, intl.locale]
  );

  /**
   * Handles route changes based on the selected value from the bottom navigation.
   *
   * @param {number} value - The index value representing the selected route in the routeNames array.
   */
  const handleRouteChange = useCallback(
    (value) => {
      const routeName = routeNames[value];

      if (routeName === 'SearchPage') {
        handleSubmit({
          dates: null,
          location: defaultSearchCountry(intl.locale),
          state: { tab: 'map' },
        });
        return;
      }

      let pathParams = {};
      if (routeName === 'InboxPage') pathParams = { tab: 'orders' }; // replace 'defaultTabName' with the name of the tab you want to display

      const url = createResourceLocatorString(
        routeName,
        routeConfiguration(),
        pathParams,
        {},
        intl.locale
      );
      navigate(url);
    },
    [handleSubmit, navigate, intl.locale, routeNames]
  );

  /**
   * Handles the change event when a navigation item is clicked.
   *
   * @param {number} value - The index value representing the selected navigation item.
   */
  const handleChange = useCallback(
    (value) => {
      if (value === 4) {
        setSiteValue(!mobileMenuState ? 4 : -1);
        dispatch(setMobileMenuState(!mobileMenuState));
        setOpenDrawer(null);
      } else if (!isAuthenticated && (value === 1 || value === 3)) {
        const drawerType = value === 1 ? 'bookmarks' : 'bookings';
        setOpenDrawer(drawerType);
      } else {
        handleRouteChange(value);
        setOpenDrawer(null);
        dispatch(setMobileMenuState(false));
      }
    },
    [isAuthenticated, mobileMenuState, handleRouteChange, dispatch]
  );

  /**
   * Handles the change event of the BottomNavigation component.
   *
   * @param {object} event - The event object.
   * @param {number} newValue - The new value selected in the BottomNavigation.
   */
  const handleChangeBottomNavigation = useCallback(
    (event, newValue) => {
      setSiteValue(newValue);
      handleChange(newValue);
    },
    [handleChange]
  );

  const bottomNavigationStyle = useMemo(
    () => getBottomNavigationStyle(theme.zIndex.drawer),
    [theme.zIndex.drawer]
  );

  return (
    <Portal>
      <Box sx={{ zIndex: 2 }}>
        <Drawer
          anchor="bottom"
          open={mobileMenuState}
          {...(settingsMenuState ? { transitionDuration: 0 } : {})}
        >
          <Box sx={{ height: '100%', minHeight: '100vh', backgroundColor: '#fff' }}>
            <MobileMenu onClose={() => dispatch(setMobileMenuState(false))} />
          </Box>
        </Drawer>
        <Drawer anchor="bottom" open={openDrawer === 'becomeHost'} transitionDuration={0}>
          <Box sx={boxStylesForCTAs}>
            <NoSpotsRegisterCTA />
          </Box>
        </Drawer>
        <Drawer anchor="bottom" open={openDrawer === 'bookmarks'} transitionDuration={0}>
          <Box sx={boxStylesForCTAs}>
            <NoBookmarksRegisterCTA />
          </Box>
        </Drawer>
        <Drawer anchor="bottom" open={openDrawer === 'bookings'} transitionDuration={0}>
          <Box sx={boxStylesForCTAs}>
            <NoBookingsRegisterCTA />
          </Box>
        </Drawer>

        <BottomNavigation
          onChange={handleChangeBottomNavigation}
          showLabels
          sx={bottomNavigationStyle}
          value={siteValue}
        >
          <BottomNavigationAction
            aria-label={intl.formatMessage({ id: 'BottomNavigationBar.home' })}
            disableRipple
            icon={<IconHome color={getIconColor(siteValue, 0)} fontSize="large" />}
            label={
              <Typography sx={getIconTextStyle(siteValue, 0)}>
                <FormattedMessage id="BottomNavigationBar.home" />
              </Typography>
            }
          />
          <BottomNavigationAction
            aria-label={intl.formatMessage({ id: 'BottomNavigationBar.favorites' })}
            disableRipple
            icon={<IconFavorites color={getIconColor(siteValue, 1)} fontSize="large" />}
            label={
              <Typography sx={getIconTextStyle(siteValue, 1)}>
                <FormattedMessage id="BottomNavigationBar.favorites" />
              </Typography>
            }
          />
          <BottomNavigationAction
            aria-label={intl.formatMessage({ id: 'BottomNavigationBar.search' })}
            disableRipple
            icon={<SearchIconAction isActive={siteValue === 2} />}
            label={
              <Typography sx={getIconTextStyle(siteValue, 2)}>
                <FormattedMessage id="BottomNavigationBar.search" />
              </Typography>
            }
            sx={{ transform: 'translateY(-20px)' }}
          />
          <BottomNavigationAction
            aria-label={intl.formatMessage({ id: 'BottomNavigationBar.bookings' })}
            disableRipple
            icon={
              <Badge badgeContent={notificationCount} color="primary">
                <IconBookings color={getIconColor(siteValue, 3)} fontSize="large" />
              </Badge>
            }
            label={
              <Typography sx={getIconTextStyle(siteValue, 3)}>
                <FormattedMessage id="BottomNavigationBar.bookings" />
              </Typography>
            }
          />
          <BottomNavigationAction
            aria-label={intl.formatMessage({ id: 'BottomNavigationBar.more' })}
            disableRipple
            icon={<IconMore color={getIconColor(siteValue, 4, mobileMenuState)} fontSize="large" />}
            label={
              <Typography sx={getIconTextStyle(siteValue, 4, mobileMenuState)}>
                <FormattedMessage id="BottomNavigationBar.more" />
              </Typography>
            }
          />
        </BottomNavigation>
      </Box>
    </Portal>
  );
};

BottomNavigationBarComponent.displayName = 'BottomNavigationBar';
const BottomNavigationBar = memo(BottomNavigationBarComponent);

export default BottomNavigationBar;
